import { BREAKPOINTS, SELECTORS } from '../utils/constants';
import addToCart from '../lib/addToCart';
import renderMinicart from '../lib/renderMinicart';

const ARIA_CURRENT = 'aria-current';

const setLoadingState = (buttonEl, isLoading) => {
  const spinnerEl = buttonEl.querySelector(SELECTORS.LOADING_SPINNER);
  buttonEl.classList.toggle('pointer-events-none', isLoading);
  buttonEl.classList.toggle('opacity-50', isLoading);
  if (spinnerEl) spinnerEl.classList.toggle('hidden', !isLoading);
};

const initCardAtc = atcButtonEl => {
  if (!atcButtonEl) return;
  let isLoading = false;

  atcButtonEl.addEventListener('click', async () => {
    const variantId = Number(atcButtonEl.dataset.cardAtc);
    if (isLoading || !variantId) return;

    isLoading = true;
    setLoadingState(atcButtonEl, true);
    try {
      const result = await addToCart(variantId, 1);
      // addToCart swallows network errors (undefined) and resolves error
      // responses like 422 as {status} — only open the minicart on success
      if (!result || result.status) {
        console.error('Add to cart failed:', result);
        return;
      }
      await renderMinicart();
      window.theme.minicart?.open();
    } catch (error) {
      console.error('Error adding variant to cart:', error);
    } finally {
      isLoading = false;
      setLoadingState(atcButtonEl, false);
    }
  });
};

const initCardSwatches = (cardEl, atcButtonEl, desktopMediaQuery) => {
  const swatchEls = cardEl.querySelectorAll('[data-card-swatch]');
  if (!swatchEls.length) return;

  const imageEl = cardEl.querySelector('[data-card-image] img');
  const secondaryImageEl = cardEl.querySelector('[data-card-image-secondary]');
  const linkEls = cardEl.querySelectorAll('[data-card-link]');
  const priceEl = cardEl.querySelector('[data-card-price]');

  const defaultState = {
    imageSrc: imageEl?.src,
    imageSrcset: imageEl?.getAttribute('srcset'),
    priceText: priceEl?.textContent.trim(),
    atcText: atcButtonEl?.querySelector('[data-atc-label]')?.textContent.trim(),
    currentSwatchEl: cardEl.querySelector('[data-card-swatch][aria-current="true"]')
  };

  const showVariant = swatchEl => {
    swatchEls.forEach(el => el.setAttribute(ARIA_CURRENT, 'false'));
    swatchEl.setAttribute(ARIA_CURRENT, 'true');

    const { swatchImage, variantPrice } = swatchEl.dataset;
    if (imageEl && swatchImage) {
      imageEl.src = swatchImage;
      imageEl.removeAttribute('srcset');
      // A variant preview replaces the generic second-image hover swap
      if (secondaryImageEl) secondaryImageEl.hidden = true;
    }
    if (priceEl && variantPrice) priceEl.textContent = variantPrice;
  };

  // Desktop hover is a temporary preview — fall back to the product's own
  // images once the pointer leaves the swatch
  const clearPreview = () => {
    if (imageEl && defaultState.imageSrc) {
      imageEl.src = defaultState.imageSrc;
      if (defaultState.imageSrcset) imageEl.setAttribute('srcset', defaultState.imageSrcset);
    }
    if (secondaryImageEl) secondaryImageEl.hidden = false;
    if (priceEl && defaultState.priceText) priceEl.textContent = defaultState.priceText;
    swatchEls.forEach(el => el.setAttribute(ARIA_CURRENT, 'false'));
    defaultState.currentSwatchEl?.setAttribute(ARIA_CURRENT, 'true');
  };

  // Mobile tap is a persistent selection — card links and quick add follow
  // it, and it becomes the state clearPreview restores to
  const selectSwatch = swatchEl => {
    showVariant(swatchEl);
    const { swatchImage, variantPrice, variantId, variantAvailable } = swatchEl.dataset;
    if (swatchImage) {
      defaultState.imageSrc = swatchImage;
      defaultState.imageSrcset = null;
    }
    if (variantPrice) defaultState.priceText = variantPrice;
    defaultState.currentSwatchEl = swatchEl;
    if (atcButtonEl && variantId) {
      const isAvailable = variantAvailable === 'true';
      const atcLabelEl = atcButtonEl.querySelector('[data-atc-label]');
      atcButtonEl.dataset.cardAtc = variantId;
      atcButtonEl.disabled = !isAvailable;
      if (atcLabelEl) {
        atcLabelEl.textContent = isAvailable ? defaultState.atcText : window.theme.strings.soldOut;
      }
    }
    linkEls.forEach(linkEl => {
      linkEl.href = swatchEl.href;
    });
  };

  swatchEls.forEach(swatchEl => {
    swatchEl.addEventListener('mouseenter', () => {
      if (desktopMediaQuery.matches) showVariant(swatchEl);
    });
    swatchEl.addEventListener('mouseleave', () => {
      if (desktopMediaQuery.matches) clearPreview();
    });
    swatchEl.addEventListener('focus', () => {
      if (desktopMediaQuery.matches) showVariant(swatchEl);
    });
    swatchEl.addEventListener('blur', () => {
      if (desktopMediaQuery.matches) clearPreview();
    });
    swatchEl.addEventListener('click', event => {
      if (!desktopMediaQuery.matches) {
        event.preventDefault();
        selectSwatch(swatchEl);
      }
    });
  });
};

const initProductCards = () => {
  const cardEls = document.querySelectorAll('[data-product-card]');
  if (!cardEls.length) return;

  const desktopMediaQuery = window.matchMedia(`(min-width: ${BREAKPOINTS.DESKTOP}px)`);

  cardEls.forEach(cardEl => {
    if (cardEl.dataset.cardInit) return;
    cardEl.dataset.cardInit = 'true';

    const atcButtonEl = cardEl.querySelector('[data-card-atc]');
    initCardAtc(atcButtonEl);
    initCardSwatches(cardEl, atcButtonEl, desktopMediaQuery);
  });
};

// Public hook for scripts outside the theme bundle: Bloomreach injects cards
// fetched from ?view=card after page load and calls this to bind them.
// initProductCards skips cards it has already bound, so repeat calls are safe.
const initProductCardsApi = () => {
  if (!window.theme || window.theme.productCards) return;
  window.theme.productCards = { init: initProductCards };
};

export { initProductCardsApi };
export default initProductCards;
